import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputLeftAddon,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const FormularioRuta: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    nombre: '',
    tipo: '',
    descripcion: '',
    puntos: '',
    comunidades: '',
    duracion: '',
    costo: '',
    cupo: '',
    calendario: '',
    estatus: 'activo',
  });
  const [guardando, setGuardando] = useState(false);
  const [fotosExistentes, setFotosExistentes] = useState<string[]>([]);
  const [fotosNuevas, setFotosNuevas] = useState<File[]>([]);

  useEffect(() => {
    if (!data) return;
    setForm({
      nombre: data.nombre || '',
      tipo: data.tipo || '',
      descripcion: data.descripcion || '',
      puntos: Array.isArray(data.puntos) ? data.puntos.join(', ') : data.puntos || '',
      comunidades: Array.isArray(data.comunidades) ? data.comunidades.join(', ') : data.comunidades || '',
      duracion: data.duracion || '',
      costo: data.costo || '',
      cupo: data.cupo ?? '',
      calendario: Array.isArray(data.calendario) ? data.calendario.join(', ') : data.calendario || '',
      estatus: data.estatus || 'activo',
    });
    setFotosExistentes(Array.isArray(data.fotos) ? data.fotos.filter(Boolean) : []);
    setFotosNuevas([]);
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const toArray = (value: string) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);

  const handleFotosSeleccionadas = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setFotosNuevas((prev) => [...prev, ...files]);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.nombre) return;
    setGuardando(true);
    try {
      const fd = new FormData();
      fd.append('nombre', form.nombre || '');
      fd.append('tipo', form.tipo || '');
      fd.append('descripcion', form.descripcion || '');
      fd.append('puntos', JSON.stringify(form.puntos ? toArray(form.puntos) : []));
      fd.append('comunidades', JSON.stringify(form.comunidades ? toArray(form.comunidades) : []));
      fd.append('duracion', form.duracion || '');
      fd.append('costo', form.costo || '');
      fd.append('cupo', form.cupo || '');
      fd.append('calendario', JSON.stringify(form.calendario ? toArray(form.calendario) : []));
      fd.append('fotosExistentes', JSON.stringify(fotosExistentes));
      fd.append('estatus', form.estatus || 'activo');
      fotosNuevas.forEach((file) => fd.append('fotos', file));

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/rutas/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/rutas', fd);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <Stack spacing={5}>
        <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} bg="white">
          <Text fontWeight="semibold" color="#5b182b" mb={3}>Datos generales</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Nombre</FormLabel>
              <Input value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Tipo</FormLabel>
              <Input value={form.tipo} onChange={(e) => handleChange('tipo', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Descripcion</FormLabel>
              <Textarea value={form.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} isDisabled={disabled} rows={3} />
            </FormControl>
            <FormControl>
              <FormLabel>Duracion</FormLabel>
              <Input value={form.duracion} onChange={(e) => handleChange('duracion', e.target.value)} isDisabled={disabled} placeholder="Ej. 2 horas / medio dia" />
            </FormControl>
            <FormControl>
              <FormLabel>Costo</FormLabel>
              <InputGroup>
                <InputLeftAddon>$</InputLeftAddon>
                <Input type="number" value={form.costo} onChange={(e) => handleChange('costo', e.target.value)} isDisabled={disabled} />
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Cupo</FormLabel>
              <Input type="number" value={form.cupo} onChange={(e) => handleChange('cupo', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Estatus</FormLabel>
              <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} bg="white">
          <Text fontWeight="semibold" color="#5b182b" mb={3}>Recorrido y calendario</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Puntos de la ruta</FormLabel>
              <Textarea value={form.puntos} onChange={(e) => handleChange('puntos', e.target.value)} isDisabled={disabled} rows={3} />
              <FormHelperText>Separar por coma.</FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel>Comunidades</FormLabel>
              <Textarea value={form.comunidades} onChange={(e) => handleChange('comunidades', e.target.value)} isDisabled={disabled} rows={3} />
              <FormHelperText>Separar por coma.</FormHelperText>
            </FormControl>
            <FormControl gridColumn={{ md: '1 / -1' }}>
              <FormLabel>Calendario / fechas</FormLabel>
              <Textarea
                value={form.calendario}
                onChange={(e) => handleChange('calendario', e.target.value)}
                isDisabled={disabled}
                rows={3}
                placeholder="Ej. Sabados 10:00, Temporada vacacional"
              />
              <FormHelperText>Separar por coma para registrar varias fechas u horarios.</FormHelperText>
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={4} bg="white">
          <Text fontWeight="semibold" color="#5b182b" mb={3}>Multimedia</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Fotos de la ruta</FormLabel>
              {!disabled ? (
                <>
                  <Input type="file" accept="image/*,.pdf" multiple onChange={handleFotosSeleccionadas} p={1} />
                  <FormHelperText>Sube imagenes o PDF, no URLs.</FormHelperText>
                </>
              ) : (
                <Text fontSize="sm" color="gray.500">Solo lectura</Text>
              )}
            </FormControl>
            <FormControl>
              <FormLabel>Nuevos archivos seleccionados</FormLabel>
              {fotosNuevas.length === 0 ? (
                <Text fontSize="sm" color="gray.500">Sin archivos nuevos.</Text>
              ) : (
                <Wrap>
                  {fotosNuevas.map((file, idx) => (
                    <WrapItem key={`${file.name}-${idx}`}>
                      <Tag colorScheme="blue" borderRadius="full">
                        <TagLabel>{file.name}</TagLabel>
                        {!disabled && <TagCloseButton onClick={() => setFotosNuevas((prev) => prev.filter((_, i) => i !== idx))} />}
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              )}
            </FormControl>
          </SimpleGrid>

          <Box mt={4}>
            <Text fontWeight="medium" fontSize="sm" mb={2}>Archivos registrados</Text>
            {fotosExistentes.length === 0 ? (
              <Text fontSize="sm" color="gray.500">Sin archivos registrados.</Text>
            ) : (
              <Wrap>
                {fotosExistentes.map((foto) => (
                  <WrapItem key={foto}>
                    <Tag colorScheme="orange" borderRadius="full">
                      <TagLabel>
                        <Link href={resolveAssetUrl(foto)} isExternal>
                          {foto.split('/').pop()}
                        </Link>
                      </TagLabel>
                      {!disabled && <TagCloseButton onClick={() => setFotosExistentes((prev) => prev.filter((f) => f !== foto))} />}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </Box>
        </Box>
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioRuta;
